/**
 * If you need to add new languages, this list helps with getting ISO 639-1/2/2T/2B/3 codes:
 * https://iso639-3.sil.org/code_tables/639/data
 */
import config from "../config";
import { findByPropFn, findByPropValueFn } from "./helpers";

export interface Language {
  /** IEFT (BCP 47) code */
  locale: string | null;
  /** ISO 639-1 language code (2 characters, one per language or ISO 639 macrolanguage) */
  lang2: string;
  /** ISO 639-2/2T (3 characters, one per language) */
  lang3: string;
  /** ISO 639-2B language code (3 characters, mostly the same as 639-2T but with some derived from their English name rather than local name) */
  lang3b: string;
  /** ISO 639-3 language code (3 characters, mostly the same as 639-2T but using the canonical ISO 639 macrolanguage code) */
  lang3c: string;
  /** ISO 3166-1 Alpha 2 country code (2 characters). If `null` indicates macrolanguage or writing system, not regional dialect. */
  iso2: string | null;
  /** ISO 3166-1 Alpha 3 country code (3 characters). If `null` indicates macrolanguage or writing system, not regional dialect. */
  iso3: string | null;
  /** language name (in own language) */
  name: string;
}

const LOCALE_DELIMITER = /[-_]/;

/**
 * @TODO
 *
 * This list is in co-ordination with the available languages on the translations Google Sheet.
 *
 * It would be better to have this as a resource that the backend controls and feeds to the frontend.
 *
 * (or better as an OS package)
 */
export const languages: Language[] = [
  {
    locale: "en",
    lang2: "en",
    lang3: "eng",
    lang3b: "eng",
    lang3c: "eng",
    iso2: null,
    iso3: null,
    name: "English"
  },
  {
    locale: "en-GB",
    lang2: "en",
    lang3: "eng",
    lang3b: "eng",
    lang3c: "eng",
    iso2: "GB",
    iso3: "GBR",
    name: "English (GB)"
  },
  {
    locale: "en-US",
    lang2: "en",
    lang3: "eng",
    lang3b: "usa",
    lang3c: "eng",
    iso2: "US",
    iso3: "USA",
    name: "English (US)"
  },
  {
    locale: "en-SG",
    lang2: "en",
    lang3: "eng",
    lang3b: "eng",
    lang3c: "eng",
    iso2: "SG",
    iso3: "SGP",
    name: "English (SG)"
  },
  {
    locale: "en-CA",
    lang2: "en",
    lang3: "eng",
    lang3b: "eng",
    lang3c: "eng",
    iso2: "CA",
    iso3: "CAN",
    name: "English (Canada)"
  },
  {
    locale: "fr",
    lang2: "fr",
    lang3: "fra",
    lang3b: "fra",
    lang3c: "fra",
    iso2: null,
    iso3: null,
    name: "Français"
  },
  {
    locale: "fr-CA",
    lang2: "fr",
    lang3: "fra",
    lang3b: "fra",
    lang3c: "fra",
    iso2: null,
    iso3: null,
    name: "Français (Canada)"
  },
  {
    locale: "es",
    lang2: "es",
    lang3: "spa",
    lang3b: "spa",
    lang3c: "spa",
    iso2: null,
    iso3: null,
    name: "Español"
  },
  {
    locale: "it",
    lang2: "it",
    lang3: "ita",
    lang3b: "ita",
    lang3c: "ita",
    iso2: null,
    iso3: null,
    name: "Italiano"
  },
  {
    locale: "de",
    lang2: "de",
    lang3: "deu",
    lang3b: "ger",
    lang3c: "deu",
    iso2: null,
    iso3: null,
    name: "Deutsch"
  },
  {
    locale: "da",
    lang2: "da",
    lang3: "dan",
    lang3b: "dan",
    lang3c: "dan",
    iso2: null,
    iso3: null,
    name: "Danish"
  },
  {
    locale: "fl",
    lang2: "fl",
    lang3: "fla",
    lang3b: "fla",
    lang3c: "fla",
    iso2: null,
    iso3: null,
    name: "Flemish"
  },
  {
    locale: "nl",
    lang2: "nl",
    lang3: "nld",
    lang3b: "dut",
    lang3c: "nld",
    iso2: null,
    iso3: null,
    name: "Nederlands"
  },
  {
    locale: "sv",
    lang2: "sv",
    lang3: "swe",
    lang3b: "swe",
    lang3c: "swe",
    iso2: null,
    iso3: null,
    name: "Svenska"
  },
  {
    locale: "cs",
    lang2: "cs",
    lang3: "ces",
    lang3b: "cze",
    lang3c: "ces",
    iso2: null,
    iso3: null,
    name: "Čeština"
  },
  {
    locale: "ar",
    lang2: "ar",
    lang3: "ara",
    lang3b: "ara",
    lang3c: "ara",
    iso2: null,
    iso3: null,
    name: "العربية"
  },
  {
    locale: "zh",
    lang2: "zh",
    lang3: "zho",
    lang3b: "chi",
    lang3c: "zho",
    iso2: null,
    iso3: null,
    name: "中文"
  },
  {
    locale: "zh-Hant",
    lang2: "zh",
    lang3: "zho",
    lang3b: "chi",
    lang3c: "yue",
    iso2: null,
    iso3: null,
    name: "中文 (繁體)"
  },
  {
    locale: "zh-Hans",
    lang2: "zh",
    lang3: "zho",
    lang3b: "chi",
    lang3c: "cmn",
    iso2: null,
    iso3: null,
    name: "中文 (简体)"
  },
  {
    locale: "ja",
    lang2: "ja",
    lang3: "jpn",
    lang3b: "jpn",
    lang3c: "jpn",
    iso2: null,
    iso3: null,
    name: "日本語"
  },
  {
    locale: "ko",
    lang2: "ko",
    lang3: "kor",
    lang3b: "kor",
    lang3c: "kor",
    iso2: null,
    iso3: null,
    name: "한국어"
  },
  {
    locale: "th",
    lang2: "th",
    lang3: "tha",
    lang3b: "tha",
    lang3c: "tha",
    iso2: null,
    iso3: null,
    name: "ภาษาไทย"
  },
  {
    locale: "ru",
    lang2: "ru",
    lang3: "rus",
    lang3b: "rus",
    lang3c: "rus",
    iso2: null,
    iso3: null,
    name: "Русский язык"
  },
  {
    locale: "uk",
    lang2: "uk",
    lang3: "ukr",
    lang3b: "ukr",
    lang3c: "ukr",
    iso2: null,
    iso3: null,
    name: "Українська"
  },
  {
    locale: "hi",
    lang2: "hi",
    lang3: "hin",
    lang3b: "hin",
    lang3c: "hin",
    iso2: null,
    iso3: null,
    name: "हिन्दी"
  },
  {
    locale: "pt",
    lang2: "pt",
    lang3: "prt",
    lang3b: "prt",
    lang3c: "prt",
    iso2: null,
    iso3: null,
    name: "Português"
  },
  {
    locale: "ms",
    lang2: "ms",
    lang3: "myt",
    lang3b: "myt",
    lang3c: "myt",
    iso2: null,
    iso3: null,
    name: "Malaysia"
  }
];

/**
 * Get the name of a language based on its locale.
 *
 * If the locale doesn't exist, it will attempt to match on macro,
 * e.g.
 */
export function getNameByLocale(locale: string): string | void {
  // Get the language/locale
  const reLocale = locale.match(LOCALE_DELIMITER)
    ? new RegExp(locale.split(LOCALE_DELIMITER).join("[-_]"))
    : locale;
  const lang = languages.find(findByPropFn("locale", reLocale));
  if (lang) {
    return lang.name;
  }

  // Get the macro-language's name if the above was not found
  const macroCode = locale.match(LOCALE_DELIMITER)
    ? locale.split(LOCALE_DELIMITER)[0]
    : undefined;
  if (macroCode) {
    const macroLang = languages.find(findByPropValueFn("lang2", macroCode));
    return macroLang ? macroLang.name : undefined;
  }
}

/**
 * Check if language/locale code is considered non-roman.
 */
export function isNonRomanLanguage(locale: string): boolean {
  let macroLang = locale.match(LOCALE_DELIMITER)
    ? locale.split(LOCALE_DELIMITER)[0]
    : locale;
  return config.languages.nonRomanLanguages.includes(macroLang);
}
